import { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useChatClient } from '../providers/ChatClientProvider';
import { useSnackbarStack } from '../providers/SnackbarProvider';
import { useGlobalStore } from '../providers/GlobalStoreProvider';
import { SnackbarStatus } from '../constants/types/prop.types';
import { QueryKeys } from '../api/query.keys';
import Routes from '../router/routes.enum';

type EventHandlers = {
  handleCustomNewApplication: (event: any) => void;
  handleMessageNew: (event: any) => void;
};

// Utility functions for text truncation
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength - 1).trim()}…`;
};

const truncateTitle = (title: string): string => truncateText(title, 35);
const truncateBody = (body: string): string => truncateText(body, 100);

export const useChannelNotifications = () => {
  const { chatClient } = useChatClient();
  const { pushSnackbar } = useSnackbarStack();
  const queryClient = useQueryClient();
  const { recruiter } = useGlobalStore();
  const { t } = useTranslation('shared/common');
  const abortControllerRef = useRef<AbortController | null>(null);
  const listenersSetupRef = useRef<boolean>(false);
  const eventHandlersRef = useRef<EventHandlers | null>(null);

  useEffect(() => {
    if (!chatClient || listenersSetupRef.current) return;

    // Create abort controller for cancelling async operations
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    const setupGlobalEventListeners = async () => {
      try {
        if (signal.aborted || listenersSetupRef.current) return;

        // Create stable event handlers
        const handleCustomNewApplication = (event: any) => {
          // Invalidate ApplicationsCount query to refresh the count
          queryClient.invalidateQueries(QueryKeys.ApplicationsCount);
          queryClient.invalidateQueries(QueryKeys.ContractApplicationsList);

          // Check if new application notifications are enabled
          const isNewApplicationNotificationEnabled =
            recruiter.data?.notificationSettings?.newApplications?.inApp;

          if (!isNewApplicationNotificationEnabled) {
            return;
          }

          const contractName = event?.jobTitle;
          const applicationId = event?.applicationId;

          const text = `New application for ${contractName}!\nA new application has been submitted for ${contractName}.`;

          pushSnackbar({
            text,
            status: SnackbarStatus.Info,
            onClick: () => {
              const applicationRoute = Routes.Application.replace(':id', applicationId.toString());
              window.location.href = applicationRoute;
            },
          });
        };

        const handleMessageNew = (event: any) => {
          // Don't show notification if user is on the inbox page
          if (window.location.pathname === Routes.Inbox) {
            return;
          }

          // Check if new chat message notifications are enabled
          const isNewChatMessageNotificationEnabled =
            recruiter.data?.notificationSettings?.newChatMessages?.inApp;

          if (!isNewChatMessageNotificationEnabled) {
            return;
          }

          if (event.message?.user?.id !== chatClient?.userID) {
            const userName = event.message.user.name || t('notifications.unknown_user');
            const messageText = event.message.text || t('notifications.file_attachment');
            const channelId = event.channel?.id;

            const title = truncateTitle(t('notifications.new_message_from', { userName }));
            const body = truncateBody(messageText);

            pushSnackbar({
              text: `${title}\n${body}`,
              status: SnackbarStatus.Info,
              onClick: channelId
                ? () => {
                    window.location.href = `${Routes.Inbox}?channelId=${channelId}`;
                  }
                : undefined,
            });
          }
        };

        const handleSubscriptionUpdated = () => {
          queryClient.invalidateQueries(QueryKeys.SubscriptionMyTiers);
          queryClient.invalidateQueries(QueryKeys.AgencyMe);
        };

        // Store handlers for cleanup
        eventHandlersRef.current = {
          handleCustomNewApplication,
          handleMessageNew,
        };

        // Set up global event listeners on the chat client
        chatClient.on('custom_new_application', handleCustomNewApplication);
        chatClient.on('message.new', handleMessageNew);
        chatClient.on('notification.message_new', handleMessageNew);
        chatClient.on('custom_subscription_updated', handleSubscriptionUpdated);

        listenersSetupRef.current = true;
      } catch (error) {
        if (!signal.aborted) {
          pushSnackbar({
            text: t('notifications.connection_error'),
            status: SnackbarStatus.Error,
          });
        }
      }
    };

    setupGlobalEventListeners();

    // Cleanup function
    return () => {
      // Cancel any ongoing async operations
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Clean up event listeners
      if (eventHandlersRef.current && listenersSetupRef.current) {
        const { handleCustomNewApplication, handleMessageNew } = eventHandlersRef.current;

        chatClient.off('custom_new_application', handleCustomNewApplication);
        chatClient.off('message.new', handleMessageNew);
        chatClient.off('notification.message_new', handleMessageNew);
      }

      // Reset refs
      abortControllerRef.current = null;
      eventHandlersRef.current = null;
      listenersSetupRef.current = false;
    };
  }, [chatClient, queryClient, recruiter.data?.notificationSettings, t]);

  return {
    isConnected: !!chatClient && listenersSetupRef.current,
    chatClient,
  };
};
